import React, { useState, useEffect, useMemo } from 'react';
import Button from '../common/Button';
import { toast } from 'react-toastify';
import {
  getManageableRoles,
  getCascadingRequirements,
  getAutoAssignField,
  getRoleDisplayName,
  hasDirectorAccess
} from '../../utils/rolePermissions';

function UserForm({ user, leaders, managers, sids, siteManagers, currentUser, onSubmit, onCancel, onResetPassword, loading }) {
  const [formData, setFormData] = useState({
    fullName: '',
    nickname: '',
    email: '',
    password: '',
    mykadIC: '',
    role: '',
    startDate: '',
    leaderId: '',
    sidId: '',
    siteManagerId: '',
    managerId: ''
  });

  // Get manageable roles for current user
  const manageableRoles = useMemo(() => {
    return getManageableRoles(currentUser?.role);
  }, [currentUser?.role]);

  // Filter leaders based on selected manager (for Director creating agent)
  const filteredLeaders = useMemo(() => {
    if (!leaders) return [];

    // Director-level users must select manager first, then filter leaders by that manager
    if (hasDirectorAccess(currentUser?.role) && formData.managerId) {
      return leaders.filter(l => l.managerId === formData.managerId);
    }

    // Manager sees only their own leaders (already filtered from backend)
    return leaders;
  }, [leaders, currentUser?.role, formData.managerId]);

  // Get cascading requirements for selected role
  const cascadingRequirements = useMemo(() => {
    return getCascadingRequirements(currentUser?.role, formData.role);
  }, [currentUser?.role, formData.role]);

  // Determine which dropdowns to show
  const showManagerDropdown = cascadingRequirements.includes('managerId');
  const showLeaderDropdown = cascadingRequirements.includes('leaderId') ||
    (currentUser?.role === 'manager' && ['agent', 'telemarketer'].includes(formData.role));
  const showSidDropdown = cascadingRequirements.includes('sidId');
  const showSiteManagerDropdown = cascadingRequirements.includes('siteManagerId');

  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || '',
        nickname: user.nickname || '',
        email: user.email || '',
        password: '', // Never pre-fill password
        mykadIC: user.mykadIC || '',
        role: user.role || '',
        startDate: user.startDate || '',
        leaderId: user.leaderId || '',
        sidId: user.sidId || '',
        siteManagerId: user.siteManagerId || '',
        managerId: user.managerId || ''
      });
    } else {
      // Set default role to first manageable role
      if (manageableRoles.length > 0) {
        setFormData(prev => ({
          ...prev,
          role: manageableRoles[0]
        }));
      }
    }
  }, [user, manageableRoles]);

  // Reset dependent fields when manager changes
  useEffect(() => {
    if (hasDirectorAccess(currentUser?.role) && formData.managerId) {
      // Reset leaderId if current leader is not under selected manager
      const leaderInManager = leaders?.find(l => l.id === formData.leaderId && l.managerId === formData.managerId);
      if (formData.leaderId && !leaderInManager) {
        setFormData(prev => ({ ...prev, leaderId: '' }));
      }
    }
  }, [formData.managerId, formData.leaderId, leaders, currentUser?.role]);

  function handleChange(e) {
    const { name, value } = e.target;

    // Auto-format MyKad IC with hyphens (XXXXXX-XX-XXXX)
    if (name === 'mykadIC') {
      const cleaned = value.replace(/\D/g, ''); // Remove non-digits
      let formatted = cleaned;

      if (cleaned.length > 6) {
        formatted = cleaned.slice(0, 6) + '-' + cleaned.slice(6);
      }
      if (cleaned.length > 8) {
        formatted = cleaned.slice(0, 6) + '-' + cleaned.slice(6, 8) + '-' + cleaned.slice(8, 12);
      }

      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
    } else if (name === 'role') {
      // Reset supervisor assignments when role changes
      setFormData(prev => ({
        ...prev,
        [name]: value,
        leaderId: '',
        sidId: '',
        siteManagerId: '',
        managerId: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  }

  function handleSubmit(e) {
    e.preventDefault();

    // Validation
    if (!formData.fullName.trim()) {
      toast.error('Full name is required');
      return;
    }

    if (!formData.nickname.trim()) {
      toast.error('Nickname is required');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    if (!formData.mykadIC) {
      toast.error('MyKad IC is required');
      return;
    }

    if (!formData.role) {
      toast.error('Role is required');
      return;
    }

    // Password required for new users
    if (!user && !formData.password) {
      toast.error('Password is required');
      return;
    }

    // Validate cascading requirements
    for (const field of cascadingRequirements) {
      if (!formData[field]) {
        const fieldName = field.replace('Id', '').replace(/([A-Z])/g, ' $1').trim();
        toast.error(`Please select a ${fieldName}`);
        return;
      }
    }

    // Additional validation for Manager creating agent
    if (currentUser?.role === 'manager' && ['agent', 'telemarketer'].includes(formData.role) && !formData.leaderId) {
      toast.error('Please assign a leader for this user');
      return;
    }

    // Prepare data
    const submitData = { ...formData };

    // Remove password if empty for updates
    if (user && !submitData.password) {
      delete submitData.password;
    }

    // Apply auto-assignment based on current user's role
    const autoAssignField = getAutoAssignField(currentUser?.role, formData.role);
    if (autoAssignField) {
      submitData[autoAssignField] = currentUser.id;
    }

    // Clean up unused supervisor fields based on role
    if (!['agent', 'telemarketer'].includes(formData.role)) {
      delete submitData.leaderId;
    }
    if (!['asid', 'designer', 'draughter_2d'].includes(formData.role)) {
      delete submitData.sidId;
    }
    if (formData.role !== 'site_supervisor') {
      delete submitData.siteManagerId;
    }
    // managerId is needed for leaders (their manager) and, when a director-level
    // user creates an agent/telemarketer, the backend cascading rules require it too.
    if (!['leader', 'agent', 'telemarketer'].includes(formData.role)) {
      delete submitData.managerId;
    }

    onSubmit(submitData);
  }

  // Group roles by category for dropdown
  const roleGroups = useMemo(() => {
    const groups = {
      'Management': ['admin', 'director', 'manager'],
      'Sales Team': ['leader', 'agent', 'telemarketer'],
      'Design Team': ['sid', 'asid', 'designer', 'draughter_2d', 'draughter_site', 'quotation_team'],
      'Support Team': ['legal_team'],
      'Site Team': ['operation_head', 'site_manager', 'site_supervisor']
    };

    return Object.entries(groups)
      .map(([label, roles]) => ({
        label,
        roles: roles.filter(r => manageableRoles.includes(r))
      }))
      .filter(group => group.roles.length > 0);
  }, [manageableRoles]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Full Name */}
        <div>
          <label className="label">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            className="input"
            placeholder="John Doe"
            required
          />
        </div>

        {/* Nickname */}
        <div>
          <label className="label">
            Nickname <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="nickname"
            value={formData.nickname}
            onChange={handleChange}
            className="input"
            placeholder="Johnny"
            required
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <label className="label">
          Email Address <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className="input"
          placeholder="john@example.com"
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* MyKad IC */}
        <div>
          <label className="label">
            MyKad IC <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="mykadIC"
            value={formData.mykadIC}
            onChange={handleChange}
            className="input"
            placeholder="123456-12-1234"
            required
          />
        </div>

        {/* Start Date */}
        <div>
          <label className="label">
            Start Date
          </label>
          <input
            type="date"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            className="input"
          />
        </div>
      </div>

      {/* Password — set on create, reset-to-IC on edit */}
      {user ? (
        <div>
          <label className="label">Password</label>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onResetPassword && onResetPassword(user)}
            disabled={loading}
          >
            Reset Password to IC Number
          </Button>
          <p className="text-xs text-gray-500 mt-1">
            Resets this user's password to their IC number (digits only).
          </p>
        </div>
      ) : (
        <div>
          <label className="label">
            Password <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className="input"
            placeholder="Enter password"
            required
          />
        </div>
      )}

      {/* Role - Filtered by current user's permissions */}
      <div>
        <label className="label">
          Role <span className="text-red-500">*</span>
        </label>
        <select
          name="role"
          value={formData.role}
          onChange={handleChange}
          className="input"
          required
        >
          <option value="">Select a role</option>
          {roleGroups.map(group => (
            <optgroup key={group.label} label={group.label}>
              {group.roles.map(role => (
                <option key={role} value={role}>
                  {getRoleDisplayName(role)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Manager dropdown (Director creating leader/agent/telemarketer) */}
      {showManagerDropdown && managers && managers.length > 0 && (
        <div>
          <label className="label">
            Assign to Manager <span className="text-red-500">*</span>
          </label>
          <select
            name="managerId"
            value={formData.managerId}
            onChange={handleChange}
            className="input"
            required
          >
            <option value="">Select a manager</option>
            {managers.map(manager => (
              <option key={manager.id} value={manager.id}>
                {manager.nickname}
              </option>
            ))}
          </select>
          {['agent', 'telemarketer'].includes(formData.role) && !formData.managerId && (
            <p className="text-xs text-amber-600 mt-1">
              Select a manager first to see available leaders
            </p>
          )}
        </div>
      )}

      {/* Leader dropdown (for agents and telemarketers) */}
      {showLeaderDropdown && (
        <div>
          <label className="label">
            Assign to Leader <span className="text-red-500">*</span>
          </label>
          <select
            name="leaderId"
            value={formData.leaderId}
            onChange={handleChange}
            className="input"
            required
            disabled={hasDirectorAccess(currentUser?.role) && !formData.managerId}
          >
            <option value="">
              {hasDirectorAccess(currentUser?.role) && !formData.managerId
                ? 'Select a manager first'
                : 'Select a leader'}
            </option>
            {filteredLeaders.map(leader => (
              <option key={leader.id} value={leader.id}>
                {leader.nickname}
              </option>
            ))}
          </select>
          {filteredLeaders.length === 0 && formData.managerId && (
            <p className="text-xs text-red-500 mt-1">
              No leaders under selected manager. Please create a leader first.
            </p>
          )}
        </div>
      )}

      {/* SID dropdown (for design team members) */}
      {showSidDropdown && (
        <div>
          <label className="label">
            Assign to SID <span className="text-red-500">*</span>
          </label>
          <select
            name="sidId"
            value={formData.sidId}
            onChange={handleChange}
            className="input"
            required
          >
            <option value="">Select a SID</option>
            {sids && sids.map(sid => (
              <option key={sid.id} value={sid.id}>
                {sid.nickname}
              </option>
            ))}
          </select>
          {(!sids || sids.length === 0) && (
            <p className="text-xs text-red-500 mt-1">
              No SIDs available. Please create a SID first.
            </p>
          )}
        </div>
      )}

      {/* Site Manager dropdown (for site supervisors) */}
      {showSiteManagerDropdown && (
        <div>
          <label className="label">
            Assign to Site Manager <span className="text-red-500">*</span>
          </label>
          <select
            name="siteManagerId"
            value={formData.siteManagerId}
            onChange={handleChange}
            className="input"
            required
          >
            <option value="">Select a site manager</option>
            {siteManagers && siteManagers.map(sm => (
              <option key={sm.id} value={sm.id}>
                {sm.nickname}
              </option>
            ))}
          </select>
          {(!siteManagers || siteManagers.length === 0) && (
            <p className="text-xs text-red-500 mt-1">
              No site managers available. Please create a site manager first.
            </p>
          )}
        </div>
      )}

      {/* Info for site draughter */}
      {formData.role === 'draughter_site' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-blue-800">
              Site draughters are shared resources and can be assigned site measurement tasks by any SID. They are not assigned to a specific SID.
            </p>
          </div>
        </div>
      )}

      {/* Info for quotation team */}
      {formData.role === 'quotation_team' && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-purple-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-purple-800">
              Quotation team members report directly to the Director and work on quotations for all projects.
            </p>
          </div>
        </div>
      )}

      {/* Info for legal team */}
      {formData.role === 'legal_team' && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-slate-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-slate-800">
              Legal team members report directly to the Director and handle contracts and legal matters for all projects.
            </p>
          </div>
        </div>
      )}

      {/* Auto-assignment info */}
      {getAutoAssignField(currentUser?.role, formData.role) && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-green-800">
              This user will be automatically assigned under you.
            </p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={loading}
        >
          {user ? 'Update User' : 'Create User'}
        </Button>
      </div>
    </form>
  );
}

export default UserForm;
